import React, {useState, useMemo, useEffect} from "react";
import "chartjs-adapter-moment";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    TimeScale,
} from "chart.js";
import {Line} from "react-chartjs-2";
import {
    reduceDataToByDay,
    reduceDataToByWeek,
    reduceDataToByMonth,
    getTotal,
} from "./utilityfunctions";
import zoomPlugin from "chartjs-plugin-zoom";
import {useRef} from "react";
import {htmlLegendPlugin} from "./chartsPlugins";

import {
    getDaysDifference,
    getHoursDifference,
    getMonthsDifference,
} from "./utilityfunctions";

ChartJS.register(
    TimeScale,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Filler,
    Title,
    Tooltip,
    Legend,
    zoomPlugin
);

function OverviewChart({chartsData, dates}) {

    const [dateFrom, dateTo] = dates;

    const [timeline, setTimeline] = useState("Hourly");

    const [amounts, setAmounts] = useState(chartsData);

    const [totalChartAmount, setTotalChartAmount] = useState(0);

    const transactionsChartRef = useRef(null);

    useEffect(() => {
        if (getMonthsDifference(dateFrom, dateTo) > 1 && timeline === "Hourly") {
            setTimeline("Weekly");
            return;
        }
        if (getHoursDifference(dateFrom, dateTo) <= 24) {
            setTimeline("Hourly");
            return;
        }
        if (getDaysDifference(dateFrom, dateTo) <= 7 && (timeline === "Weekly" || timeline === "Monthly")) {
            setTimeline("Daily");
            return;
        }
        if (getMonthsDifference(dateFrom, dateTo) <= 1 && timeline === "Monthly") {
            setTimeline("Daily");
            return;
        }
    }, [dateFrom, dateTo, timeline])


    useEffect(() => {
        setTimeline("Daily");
        setAmounts(reduceDataToByDay(chartsData));
    }, [])

    useEffect(() => {
        if (timeline === "Hourly") {
            setAmounts(chartsData);
            return;
        }
        if (timeline === "Daily") {
            setAmounts(reduceDataToByDay(chartsData));
            return;
        }
        if (timeline === "Weekly") {
            setAmounts(reduceDataToByWeek(chartsData));
            return;
        }
        if (timeline === "Monthly") {
            setAmounts(reduceDataToByMonth(chartsData));
            return;
        }
    }, [timeline, chartsData]);

    // useEffect(() => {
    //   setAmounts(transactionsCurves.AmountsCurve);
    //   setTransactions(transactionsCurves.TransactionsCurve);
    // }, []);

    // const maxTicksLimit = useMemo(() => {
    //   if (new Date(dateTo).getFullYear() - new Date(dateFrom).getFullYear() > 2) {
    //     if (timeline === "Daily") return 10;
    //     if (timeline === "Monthly") return 8;
    //     if (timeline === "Weekly") return 15;
    //     if (timeline === "Hourly") return 35;
    //   } else {
    //     if (timeline === "Daily") return 30;
    //     if (timeline === "Monthly") return 12;
    //     if (timeline === "Weekly") return 20;
    //     if (timeline === "Hourly") return 50;
    //   }
    // }, [dateFrom, dateTo, timeline]);

    const unit = useMemo(() => {
        if (timeline === "Hourly") return "hour";
        if (timeline === "Daily") return "day";
        if (timeline === "Weekly") return "week";
        if (timeline === "Monthly") return "month";
    }, [timeline]);


    const options = useMemo(() => {
        return {
            animation: false,
            responsive: true,
            maintainAspectRatio: false,
            layout: {
                padding: {
                    top: 15,
                },
            },
            interaction: {
                mode: "index",
                intersect: false,
            },
            stacked: false,
            plugins: {
                title: {
                    display: false,
                    text: "Chart.js Line Chart - Multi Axis",
                },
                htmlLegend: {
                    // ID of the container to put the legend in
                    containerID: "transaction-legend",
                },
                legend: {
                    // align: "end",
                    display: false,
                },
                zoom: {
                    zoom: {
                        wheel: {
                            enabled: false,
                        },
                        drag: {
                            enabled: false,
                        },
                        pinch: {
                            enabled: false,
                        },
                        mode: "x",
                    },
                },
            },
            scales: {
                y: {
                    type: "linear",
                    display: true,
                    position: "left",
                    grid: {
                        drawOnChartArea: true,
                    },
                    min: 0,
                    title: {
                        display: true,
                    },
                    ticks: {
                        callback: (val) => `${val}`,
                    },
                },
                x: {
                    title: {
                        display: true,
                    },
                    grid: {
                        drawOnChartArea: false,
                    },
                    offest: true,
                    type: "time",
                    beginAtZero: true,
                    time: {
                        unit,
                        displayFormats: {
                            month: "MMMM YYYY",
                            day: "MMMM DD",
                        },
                        tooltipFormat: "DD/MM/YY HH:mm",
                    },
                    ticks: {
                        align: "center",
                        autoSkip: false,
                        maxTicksLimit: 10,
                        callback: function (val, index, ticks) {
                            const valueToShow = new Date(val).toString();
                            return timeline === "Hourly"
                                ? valueToShow.substring(16, 21)
                                : timeline === "Daily"
                                    ? `${valueToShow.substring(0, 4)} ${new Date(val).getDate()}`
                                    : timeline === "Weekly"
                                        ? `${valueToShow.substring(0, 4)} ${new Date(val).getDate()}`
                                        : timeline === "Monthly"
                                            ? new Date(val).getMonth()
                                            : index;
                        },
                    },
                },
            },
            elements: {
                point: {
                    radius: 0,
                    hoverRadius: 0
                }
            },
        };
    }, [unit, timeline]);

    const data = {
        datasets: [
            {
                label: "Amount",
                data: amounts,
                borderColor: "#373464",
                backgroundColor: "#3734641c",
                yAxisID: "y",
                fill: true,
            },
        ],
    };


    useEffect(() => {
        setTotalChartAmount((Math.round(getTotal(amounts) * 100) / 100).toFixed(2));
    }, [amounts]);

    const handleResetZoom = () => {
        if (transactionsChartRef && transactionsChartRef.current) {
            transactionsChartRef.current.resetZoom();
        }
    };

    return (
        <div>
            <div>
                <div id='transaction-legend' style={{"display": "none"}}></div>
            </div>

            <div className="height-300">
                <Line
                    ref={transactionsChartRef}
                    options={options}
                    data={data}
                    plugins={[htmlLegendPlugin]}
                />
            </div>
        </div>
    );
}

export default OverviewChart;
